import React, { useState, useEffect, useCallback } from 'react';

const fmt = (v) => `$${parseFloat(v || 0).toFixed(2)}`;
const fmtD = (d) => d ? new Date(d).toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—';

// Editable price points component (no JSON!)
function PricePointsEditor({ pricePoints, onChange, compact }) {
  const pp = typeof pricePoints === 'string' ? JSON.parse(pricePoints || '[]') : (pricePoints || []);
  const update = (idx, field, val) => { const n = [...pp]; n[idx] = { ...n[idx], [field]: parseFloat(val) || 0 }; onChange(n); };
  const add = () => onChange([...pp, { price: 1.99, percentage: 0 }]);
  const remove = (idx) => onChange(pp.filter((_, i) => i !== idx));
  const totalPct = pp.reduce((s, p) => s + (p.percentage || 0), 0);

  return (
    <div className="space-y-1">
      {pp.map((p, i) => (
        <div key={i} className="flex items-center gap-1">
          <span className="text-[10px] text-gray-400">$</span>
          <input type="number" step="0.01" min="0" value={p.price} onChange={e => update(i, 'price', e.target.value)} className={`${compact ? 'w-16' : 'w-20'} px-1.5 py-1 text-xs border rounded`} />
          <input type="number" min="0" max="100" value={p.percentage} onChange={e => update(i, 'percentage', e.target.value)} className={`${compact ? 'w-12' : 'w-16'} px-1.5 py-1 text-xs border rounded`} />
          <span className="text-[10px] text-gray-400">%</span>
          {pp.length > 1 && <button onClick={() => remove(i)} className="text-red-400 hover:text-red-600 text-xs">✕</button>}
        </div>
      ))}
      <div className="flex items-center gap-2">
        <button onClick={add} className="text-[10px] text-blue-600 hover:text-blue-800">+ Add</button>
        {totalPct !== 100 && <span className="text-[10px] text-red-500">Total: {totalPct}% (should be 100%)</span>}
      </div>
    </div>
  );
}

export default function AutoFriendliesSS() {
  const [tab, setTab] = useState('transactions');
  const [stats, setStats] = useState({});
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [perPage, setPerPage] = useState(50);
  const [page, setPage] = useState(0);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [entryDetail, setEntryDetail] = useState(null);
  const [searchParams, setSearchParams] = useState({ email: '', phone: '', bin: '', last4: '', name: '', address: '', city: '', zip: '', state: '' });
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [gwConfigs, setGwConfigs] = useState([]);
  const [configLoading, setConfigLoading] = useState(false);
  const [saving, setSaving] = useState({});
  const [localEdits, setLocalEdits] = useState({}); // buffered edits per merchantId
  const [presetData, setPresetData] = useState({ enabled: true, txnCount: 2, visaPercentage: 80, pricePoints: [{ price: 3.99, percentage: 50 }, { price: 7.99, percentage: 50 }] });
  const [applyingPreset, setApplyingPreset] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      params.set('limit', perPage);
      params.set('offset', page * perPage);
      const [statsRes, entriesRes] = await Promise.all([
        fetch('/api/friendly-ss/stats', { credentials: 'include' }).then(r => r.json()),
        fetch(`/api/friendly-ss?${params}`, { credentials: 'include' }).then(r => r.json()),
      ]);
      setStats(statsRes);
      setEntries(entriesRes.entries || []);
      setTotal(entriesRes.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [statusFilter, perPage, page]);

  useEffect(() => { if (tab === 'transactions') { fetchData(); const i = setInterval(fetchData, 8000); return () => clearInterval(i); } }, [fetchData, tab]);
  useEffect(() => { setPage(0); }, [statusFilter, perPage]);

  const fetchConfig = async () => {
    setConfigLoading(true);
    try {
      const res = await fetch('/api/friendly-ss/config', { credentials: 'include' });
      const data = await res.json();
      setGwConfigs(data.gateways || []);
    } catch (err) { console.error(err); }
    finally { setConfigLoading(false); }
  };
  useEffect(() => { if (tab === 'config') fetchConfig(); }, [tab]);

  const handleEntryClick = async (id) => {
    if (selectedEntry === id) { setSelectedEntry(null); setEntryDetail(null); return; }
    try {
      const res = await fetch(`/api/friendly-ss/${id}`, { credentials: 'include' }).then(r => r.json());
      setSelectedEntry(id); setEntryDetail(res);
    } catch (err) { console.error(err); }
  };

  const saveGwConfig = async (merchantId, updates) => {
    setSaving(prev => ({ ...prev, [merchantId]: true }));
    try {
      await fetch(`/api/friendly-ss/config/${merchantId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      await fetchConfig();
    } catch (err) { console.error(err); }
    finally { setSaving(prev => ({ ...prev, [merchantId]: false })); }
  };

  const applyPreset = async () => {
    setApplyingPreset(true);
    try {
      await fetch('/api/friendly-ss/config/preset', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(presetData)
      });
      await fetchConfig();
    } catch (err) { console.error(err); }
    finally { setApplyingPreset(false); }
  };

  const sBadge = (s) => {
    const c = { pending: 'bg-yellow-100 text-yellow-800', processing: 'bg-blue-100 text-blue-800', completed: 'bg-green-100 text-green-800', failed: 'bg-red-100 text-red-800', no_cardholder: 'bg-orange-100 text-orange-800' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c[s] || 'bg-gray-100 text-gray-800'}`}>{s}</span>;
  };
  const tBadge = (t) => {
    const c = { visa: 'bg-blue-100 text-blue-700', mastercard: 'bg-orange-100 text-orange-700' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c[t] || 'bg-gray-100 text-gray-700'}`}>{t?.toUpperCase()}</span>;
  };

  const totalPages = Math.ceil(total / perPage);
  const e = entryDetail?.entry;
  const ch = entryDetail?.cardholder;

  if (loading && tab === 'transactions') return <div className="flex items-center justify-center h-64"><div className="text-gray-500">Loading...</div></div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">🛒 Auto Friendlies SS</h1>
          <p className="text-sm text-gray-500">Straight Sale friendlies triggered by checkout approved orders</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setTab('transactions')} className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'transactions' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>📋 Transactions</button>
          <button onClick={() => setTab('config')} className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === 'config' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>⚙️ Configuration</button>
        </div>
      </div>

      {/* ═══ CONFIGURATION TAB ═══ */}
      {tab === 'config' && (
        <div className="space-y-6">
          {/* Preset */}
          <div className="bg-white rounded-lg border border-purple-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-purple-700">🎯 Preset — Apply to All Gateways</h3>
              <button onClick={applyPreset} disabled={applyingPreset} className="px-5 py-2 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700 disabled:opacity-50">{applyingPreset ? '⏳ Applying...' : '🎯 Apply to All'}</button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <label className="text-[10px] text-gray-500 uppercase block mb-1">Status</label>
                <select value={presetData.enabled ? '1' : '0'} onChange={e => setPresetData({ ...presetData, enabled: e.target.value === '1' })} className="w-full px-2 py-1.5 text-xs border rounded">
                  <option value="1">✅ Enabled</option>
                  <option value="0">❌ Disabled</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] text-gray-500 uppercase block mb-1">Txn Count</label>
                <input type="number" min="1" max="10" value={presetData.txnCount} onChange={e => setPresetData({ ...presetData, txnCount: parseInt(e.target.value) || 2 })} className="w-full px-2 py-1.5 text-xs border rounded" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 uppercase block mb-1">Visa %</label>
                <input type="number" min="0" max="100" value={presetData.visaPercentage} onChange={e => setPresetData({ ...presetData, visaPercentage: parseFloat(e.target.value) || 0 })} className="w-full px-2 py-1.5 text-xs border rounded" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 uppercase block mb-1">MC %</label>
                <div className="px-2 py-1.5 text-xs bg-orange-50 border border-orange-200 rounded text-orange-700 font-medium">{(100 - (presetData.visaPercentage || 0)).toFixed(0)}%</div>
              </div>
              <div>
                <label className="text-[10px] text-gray-500 uppercase block mb-1">Price Points</label>
                <PricePointsEditor pricePoints={presetData.pricePoints} onChange={pp => setPresetData({ ...presetData, pricePoints: pp })} />
              </div>
            </div>
          </div>

          {/* Gateway List */}
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-700">📡 Gateway Configuration ({gwConfigs.length})</h3>
            <button onClick={fetchConfig} className="text-xs text-gray-500 hover:text-gray-700">🔄 Refresh</button>
          </div>

          {configLoading ? <div className="text-gray-500 text-center py-8">Loading configs...</div> : gwConfigs.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-700">
              No gateways with a Gateway ID found. Go to <strong>Gateways</strong> and add a <strong>Gateway ID</strong> (= CheckoutChamp forceMerchantId) to your gateways.
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {gwConfigs.map((gw, i) => {
                const cfg = gw.config;
                const mid = gw.gatewayId;
                const local = localEdits[mid] || {};
                const enabled = local.enabled !== undefined ? local.enabled : cfg.enabled;
                const txnCount = local.txnCount !== undefined ? local.txnCount : cfg.txnCount;
                const visa = local.visaPercentage !== undefined ? local.visaPercentage : (cfg.visaPercentage ?? 80);
                const mc = 100 - visa;
                const pp = local.pricePoints !== undefined ? local.pricePoints : (typeof cfg.pricePoints === 'string' ? JSON.parse(cfg.pricePoints) : (cfg.pricePoints || []));
                const hasChanges = Object.keys(local).length > 0;

                const setLocal = (field, val) => setLocalEdits(prev => ({ ...prev, [mid]: { ...prev[mid], [field]: val } }));

                const handleSave = async () => {
                  const updates = {};
                  if (local.enabled !== undefined) updates.enabled = local.enabled;
                  if (local.txnCount !== undefined) updates.txnCount = local.txnCount;
                  if (local.visaPercentage !== undefined) updates.visaPercentage = local.visaPercentage;
                  if (local.pricePoints !== undefined) updates.pricePoints = local.pricePoints;
                  if (Object.keys(updates).length > 0) {
                    await saveGwConfig(mid, updates);
                    setLocalEdits(prev => { const n = { ...prev }; delete n[mid]; return n; });
                  }
                };

                return (
                  <div key={i} className={`bg-white rounded-lg border-2 p-4 shadow-sm transition-all ${enabled ? 'border-green-200' : 'border-red-200 opacity-60'} ${hasChanges ? 'ring-2 ring-yellow-300' : ''}`}>
                    {/* Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-sm font-bold text-gray-800">{gw.gatewayName}</p>
                        <p className="text-[10px] text-gray-400 font-mono">MID: {mid}</p>
                      </div>
                      <button onClick={() => setLocal('enabled', !enabled)}
                        className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${enabled ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-red-100 text-red-700 hover:bg-red-200'}`}>
                        {enabled ? '✅ ON' : '❌ OFF'}
                      </button>
                    </div>

                    {/* Settings Row */}
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      <div>
                        <label className="text-[10px] text-gray-500 block">Txn Count</label>
                        <input type="number" min="1" max="10" value={txnCount} onChange={e => setLocal('txnCount', parseInt(e.target.value) || 2)} className="w-full px-2 py-1 text-xs border rounded" />
                      </div>
                      <div>
                        <label className="text-[10px] text-blue-600 block font-medium">Visa %</label>
                        <input type="number" min="0" max="100" value={visa} onChange={e => setLocal('visaPercentage', parseFloat(e.target.value) || 0)} className="w-full px-2 py-1 text-xs border border-blue-200 rounded bg-blue-50" />
                      </div>
                      <div>
                        <label className="text-[10px] text-orange-600 block font-medium">MC %</label>
                        <div className="px-2 py-1 text-xs bg-orange-50 border border-orange-200 rounded text-orange-700 font-bold">{mc.toFixed(0)}%</div>
                      </div>
                      <div className="flex items-end">
                        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 float-left" style={{ width: `${visa}%` }} />
                          <div className="h-full bg-orange-500 float-left" style={{ width: `${mc}%` }} />
                        </div>
                      </div>
                    </div>

                    {/* Price Points */}
                    <div>
                      <label className="text-[10px] text-gray-500 block mb-1">Price Points</label>
                      <PricePointsEditor compact pricePoints={pp} onChange={newPP => setLocal('pricePoints', newPP)} />
                    </div>

                    {/* Save Button */}
                    <div className="mt-3 pt-3 border-t border-gray-100 flex justify-end">
                      <button onClick={handleSave} disabled={!hasChanges || saving[mid]}
                        className={`px-5 py-1.5 rounded-lg text-xs font-medium transition-colors ${hasChanges ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}>
                        {saving[mid] ? '⏳ Saving...' : hasChanges ? '💾 Save' : 'No changes'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ═══ TRANSACTIONS TAB ═══ */}
      {tab === 'transactions' && (
        <div className="space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {[
              { l: 'Total', v: stats.total || 0, c: 'text-gray-900' },
              { l: 'Pending', v: stats.pending || 0, c: 'text-yellow-600' },
              { l: 'Completed', v: stats.completed || 0, c: 'text-green-600' },
              { l: 'Failed', v: stats.failed || 0, c: 'text-red-600' },
              { l: 'No Card', v: stats.noCardholder || 0, c: 'text-orange-600' },
              { l: 'Today ✅', v: stats.todayCompleted || 0, c: 'text-green-600' },
              { l: 'Today ❌', v: stats.todayFailed || 0, c: 'text-red-600' },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                <p className="text-xs text-gray-500 uppercase">{s.l}</p>
                <p className={`text-2xl font-bold ${s.c}`}>{s.v}</p>
              </div>
            ))}
          </div>

          {/* Search */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-700">Search SS Transactions</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
              {[['email','Email','john@example.com'],['phone','Phone','212-555-...'],['bin','BIN (6)','411111'],['last4','Last 4','1234'],['name','Name','John Doe'],['address','Address','123 Main St'],['city','City','New York'],['state','State','NY'],['zip','ZIP','10001']].map(([key, label, ph]) => (
                <div key={key}>
                  <label className="text-[10px] text-gray-500 uppercase">{label}</label>
                  <input type="text" value={searchParams[key]} onChange={ev => setSearchParams({ ...searchParams, [key]: ev.target.value })} placeholder={ph} className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 outline-none" />
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={async () => { const has = Object.values(searchParams).some(v => v.trim()); if (!has) { alert('Enter at least one search parameter'); return; } setSearching(true); try { const p = new URLSearchParams(); Object.entries(searchParams).forEach(([k,v]) => { if (v.trim()) p.set(k, v.trim()); }); const r = await fetch(`/api/friendly-ss/search?${p}`, { credentials: 'include' }); const d = await r.json(); setSearchResults(d.results || []); } catch(err){console.error(err);} finally{setSearching(false);} }} disabled={searching} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50">{searching ? '⏳...' : 'Search'}</button>
              <button onClick={() => { setSearchParams({ email:'', phone:'', bin:'', last4:'', name:'', address:'', city:'', zip:'', state:'' }); setSearchResults(null); }} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-xs hover:bg-gray-300">Clear</button>
            </div>
            {searchResults !== null && <div className="text-xs font-medium text-blue-600">Showing {searchResults.length} result(s)</div>}
          </div>

          {/* Detail */}
          {selectedEntry && e && (
            <div className="bg-white rounded-lg border-2 border-blue-300 p-5 shadow-md space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-bold text-blue-600">Transaction — {e.id?.substring(0, 8)}...</h3>
                <button onClick={() => { setSelectedEntry(null); setEntryDetail(null); }} className="text-xs text-gray-400 hover:text-gray-600">✕</button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                {[['Status', sBadge(e.status)],['Source', <span className="text-purple-600">{e.source}</span>],['Order', <span className="font-mono">{e.sourceTxnId || '—'}</span>],['Amount', <span className="font-bold">{fmt(e.sourceAmount)}</span>],['Card', tBadge(e.cardType)],['Price', <span className="text-green-600 font-bold">{fmt(e.pricePoint)}</span>],['Gateway', e.gwName],['NMI TxnID', <span className="font-mono">{e.nmiTxnId || '—'}</span>],['NMI Resp', <span className={e.nmiResponseCode==='100'?'text-green-600':'text-red-600'}>{e.nmiResponseCode} {e.nmiResponseText}</span>],['Created', fmtD(e.createdAt)],['Processed', fmtD(e.processedAt)]].map(([l,v],i) => (
                  <div key={i} className="bg-gray-50 rounded px-3 py-2"><div className="text-[10px] text-gray-500 uppercase">{l}</div><div className="mt-0.5">{v}</div></div>
                ))}
              </div>
              {ch && (
                <div className="border-t pt-3">
                  <h4 className="text-xs font-bold text-gray-600 mb-2">Cardholder</h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                    {[['Name',`${ch.firstName} ${ch.lastName}`],['Email',ch.email],['Phone',ch.phone],['Last 4',ch.cardLast4],['Type',ch.cardType?.toUpperCase()],['IP',ch.ipAddress],['Address',ch.address],['City',ch.city],['State',ch.state],['ZIP',ch.zipCode]].map(([l,v],i) => (
                      <div key={i} className="bg-blue-50 rounded px-2 py-1.5"><div className="text-[10px] text-gray-500">{l}</div><div className="font-mono text-[11px]">{v||'—'}</div></div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Filters */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {['', 'pending', 'completed', 'failed', 'no_cardholder'].map(s => (
                <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${statusFilter === s ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {s === '' ? 'All' : s === 'no_cardholder' ? 'No Card' : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
            <select value={perPage} onChange={e => setPerPage(parseInt(e.target.value))} className="bg-gray-100 text-xs rounded-lg px-3 py-1.5 border">
              {[25, 50, 100, 250].map(n => <option key={n} value={n}>{n}/page</option>)}
            </select>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b flex justify-between items-center">
              <span className="text-sm font-semibold text-gray-700">{searchResults ? `🔍 (${searchResults.length})` : `SS Transactions (${total})`}</span>
              <div className="flex items-center space-x-2">
                <button onClick={() => setPage(Math.max(0, page-1))} disabled={page===0} className="px-2 py-1 bg-gray-100 rounded text-xs disabled:opacity-30">←</button>
                <span className="text-xs text-gray-500">{page+1}/{totalPages||1}</span>
                <button onClick={() => setPage(Math.min(totalPages-1, page+1))} disabled={page>=totalPages-1} className="px-2 py-1 bg-gray-100 rounded text-xs disabled:opacity-30">→</button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="bg-gray-50 text-gray-500 uppercase">
                  <th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Card</th><th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2 text-left">Gateway</th><th className="px-3 py-2 text-left">Cardholder</th><th className="px-3 py-2 text-left">Last4</th><th className="px-3 py-2 text-left">BIN</th><th className="px-3 py-2 text-left">NMI TxnID</th><th className="px-3 py-2 text-left">Order</th><th className="px-3 py-2 text-left">Status</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {(searchResults||entries).length === 0 ? (
                    <tr><td colSpan={10} className="px-4 py-8 text-center text-gray-400">{searchResults ? 'No results' : 'No SS transactions yet'}</td></tr>
                  ) : (searchResults||entries).map(entry => (
                    <tr key={entry.id} className={`hover:bg-blue-50 cursor-pointer ${selectedEntry===entry.id?'bg-blue-50':''}`} onClick={() => handleEntryClick(entry.id)}>
                      <td className="px-3 py-2 text-gray-600">{fmtD(entry.createdAt)}</td>
                      <td className="px-3 py-2">{tBadge(entry.cardType)}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold">{fmt(entry.pricePoint)}</td>
                      <td className="px-3 py-2 text-gray-600 max-w-[140px] truncate">{entry.gwName}</td>
                      <td className="px-3 py-2">{entry.cardholderName||'—'}</td>
                      <td className="px-3 py-2 font-mono">{entry.cardholderLast4||'—'}</td>
                      <td className="px-3 py-2 font-mono">{entry.cardholderBin||'—'}</td>
                      <td className="px-3 py-2 font-mono text-gray-500">{entry.nmiTxnId||'—'}</td>
                      <td className="px-3 py-2 font-mono text-gray-500 text-[10px]">{entry.sourceTxnId?.substring(0,10)||'—'}</td>
                      <td className="px-3 py-2">{sBadge(entry.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
